import {Product} from '../../@types/wishlist.js';
import {FilterOption} from './filter-option.js';
import {FilterAction} from './filter-action.js';

export class FilterCountCache {
  private static readonly MAX_CACHE_SIZE = 20;

  private readonly cache = new Map<string, Map<string, { count: number, scoreSum: number }>>();

  public getCounts(
    products: Product[],
    filterId: string,
    options: FilterOption[],
    activeOptions: string[] = [],
    isScoringEnabled: boolean = true,
  ): Map<string, { count: number, scoreSum: number }> {
    const cacheKey = this.createCacheKey(products, filterId, activeOptions, isScoringEnabled);

    const cached = this.cache.get(cacheKey);
    if (cached !== undefined) {
      this.cache.delete(cacheKey);
      this.cache.set(cacheKey, cached);
      return cached;
    }

    const counts = this.calculateCounts(products, options, isScoringEnabled);

    if (this.cache.size >= FilterCountCache.MAX_CACHE_SIZE) {
      const oldest = this.cache.keys().next();
      if (!oldest.done) {
        this.cache.delete(oldest.value);
      }
    }
    this.cache.set(cacheKey, counts);

    return counts;
  }

  private calculateCounts(
    products: Product[],
    options: FilterOption[],
    isScoringEnabled: boolean,
  ): Map<string, { count: number, scoreSum: number }> {
    const counts = new Map<string, { count: number, scoreSum: number }>();

    options.forEach((option) => {
      counts.set(option.id, {count: 0, scoreSum: 0});
    });

    products.forEach((product) => {
      options.forEach((option) => {
        let shouldCount = true;

        if (option.action instanceof FilterAction) {
          shouldCount = option.action.filterStrategy(product);
        }

        if (shouldCount) {
          const current = counts.get(option.id) ?? {count: 0, scoreSum: 0};
          const score = isScoringEnabled ? option.calculateProductScore(product) : 0;
          counts.set(option.id, {
            count: current.count + 1,
            scoreSum: current.scoreSum + score,
          });
        }
      });
    });

    return counts;
  }

  private createCacheKey(
    products: Product[],
    filterId: string,
    activeOptions: string[],
    isScoringEnabled: boolean,
  ): string {
    const productHash = products.length > 100
      ? `${products.length}-${products.reduce((hash, p) => Math.trunc((hash * 31 + p.syan) % 2147483647), 0)}`
      : products.map(p => p.syan).join(',');
    return `${productHash}-${filterId}-${activeOptions.join(',')}-${isScoringEnabled}`;
  }

  public invalidate(): void {
    this.cache.clear();
  }
}
